import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PubService, Publicite } from '../../services/Pub.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-video-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="modal-overlay" *ngIf="isOpen" (click)="closeModal()">
      <div class="modal-content" (click)="$event.stopPropagation()">
        <button class="close-button" (click)="closeModal()">&times;</button>
        
        <!-- Loading state -->
        <div *ngIf="isLoading" class="loading-container">
          <div class="spinner"></div>
          <p>Loading video...</p>
        </div>
        
        <!-- Video content -->
        <div *ngIf="!isLoading && currentVideoAd && safeVideoUrl" class="video-container">
          <h3 *ngIf="currentVideoAd.titre" class="video-title">{{ currentVideoAd.titre }}</h3>
          <iframe [src]="safeVideoUrl" frameborder="0" allowfullscreen></iframe>
          <p *ngIf="currentVideoAd.description" class="video-description">{{ currentVideoAd.description }}</p>
        </div>
        
        <!-- Error message (only shown if there's an error but modal stays open) -->
        <div *ngIf="errorMessage" class="error-message">
          <p>{{ errorMessage }}</p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.8);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    }
    .modal-content {
      background: white;
      padding: 20px;
      border-radius: 12px;
      position: relative;
      width: 90%;
      max-width: 900px;
      max-height: 90%;
      display: flex;
      flex-direction: column;
      align-items: center;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    }
    .close-button {
      position: absolute;
      top: 15px;
      right: 15px;
      background: none;
      border: none;
      font-size: 28px;
      cursor: pointer;
      color: #666;
      transition: color 0.3s ease;
    }
    .close-button:hover {
      color: #333;
    }
    .video-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .video-title {
      margin: 0 0 15px 0;
      font-size: 24px;
      font-weight: bold;
      color: #333;
      text-align: center;
    }
    iframe {
      width: 100%;
      height: 500px;
      border: none;
      border-radius: 8px;
    }
    .video-description {
      margin: 15px 0 0 0;
      font-size: 16px;
      color: #666;
      text-align: center;
      max-width: 80%;
      line-height: 1.5;
    }
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 40px;
    }
    .spinner {
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #007bff;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 15px;
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    .error-message {
      padding: 40px;
      text-align: center;
    }
    .error-message p {
      font-size: 16px;
      color: #dc3545;
    }
    @media (max-width: 768px) {
      .modal-content {
        width: 95%;
        padding: 15px;
      }
      iframe {
        height: 300px;
      }
      .video-title {
        font-size: 20px;
      }
    }
  `]
})
export class VideoModalComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isOpen = false;
  @Input() videoUrl: string | null = null;
  @Input() pubType: string = 'VideoAcceuil';
  @Input() pageSize: number = 1;
  @Input() pageNumber: number = 1;
  @Output() close = new EventEmitter<void>();

  safeVideoUrl: SafeResourceUrl | null = null;
  currentVideoAd: Publicite | null = null;
  isLoading: boolean = false;
  errorMessage: string = '';
  private subscription: Subscription = new Subscription();

  constructor(
    private sanitizer: DomSanitizer,
    private pubService: PubService
  ) {}

  ngOnInit() {
    // Fetch video content when component initializes and modal is open
    if (this.isOpen) {
      this.fetchVideoAcceuil();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngOnChanges() {
    if (this.isOpen) {
      this.fetchVideoAcceuil();
    } else if (this.videoUrl) {
      // Backward compatibility: use provided videoUrl
      this.safeVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.videoUrl);
    } else if (!this.isOpen) {
      this.resetModal();
    }
  }

  private fetchVideoAcceuil() {
    this.isLoading = true;
    this.errorMessage = '';
    this.currentVideoAd = null;
    this.safeVideoUrl = null;


    const fetchSub = this.pubService
      .getPublicites('Pubeducanet', this.pubType, this.pageSize, this.pageNumber)
      .subscribe({
        next: (res: any) => {
          let publicites: Publicite[] = [];
          
          if (res?.results?.articlePublicitaire) {
            publicites = res.results.articlePublicitaire;
          } else if (res?.results && Array.isArray(res.results)) {
            publicites = res.results;
          } else if (Array.isArray(res)) {
            publicites = res;
          }


          if (publicites && publicites.length > 0) {
            this.currentVideoAd = publicites[0];
            
            const videoSource = this.currentVideoAd.href || this.currentVideoAd.pathvideo || this.videoUrl;
            
            if (videoSource) {
              this.safeVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(videoSource);
            } else {
              this.closeModal();
              return;
            }
          } else {
            this.closeModal();
            return;
          }
          
          this.isLoading = false;
        },
        error: (error) => {
          console.error('[VideoModal] Error fetching video content:', error);
          this.isLoading = false;
          if (this.videoUrl) {
            this.safeVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.videoUrl);
            this.errorMessage = '';
          } else {
            this.closeModal();
            return;
          }
        }
      });

    this.subscription.add(fetchSub);
  }

  private resetModal() {
    this.currentVideoAd = null;
    this.safeVideoUrl = null;
    this.isLoading = false;
    this.errorMessage = '';
  }

  closeModal() {
    this.isOpen = false;
    this.resetModal();
    this.close.emit();
  }

  // Public method to manually refresh video content
  refreshVideo() {
    if (this.isOpen) {
      this.fetchVideoAcceuil();
    }
  }
}